import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";

import Button from "components/buttons/Button";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import {
  getMirrorItem,
  MirrorItem,
  handleUpdateDraftItem,
  handleClearDraftItem,
  createMirrorItem,
  updateMirrorItem,
} from "store/mirror/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { Modal } from "components/Modal/Modal";
import SearchDropdown from "components/dropdowns/SearchDropdown/SearchDropdown";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { handleApiError } from "helpers/handleApiError";
import styles from "./MirrorCreateModal.module.scss";

const MirrorCreateModal: React.FC<{ isEdit?: boolean }> = ({
  isEdit = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isItemFetched = useAppSelector(
    (store) => store["mirror"].isItemFetched,
  );
  const isItemFetching = useAppSelector(
    (store) => store["mirror"].isItemFetching,
  );

  const [loadFailed, setLoadFailed] = useState(false);
  const countries = useAppSelector((store) => store.settings.countries);
  const payload = useAppSelector((store) => store["mirror"].draftItem);
  const entityId = searchParams.get("entityId") ?? "";

  const handleClose = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
    dispatch(handleClearDraftItem());
  };

  const handleSave = async () => {
    if (isItemFetching || (isEdit && !isItemFetched)) return;
    if (!payload?.title?.trim() || !payload?.domain?.trim()) {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("emptyRequiredFields"),
          type: NotificationTypes.error,
        }),
      );
      return;
    }

    dispatch(
      (isEdit ? updateMirrorItem : createMirrorItem)({
        title: payload?.title ? payload.title.trim() : "",
        domain: payload?.domain ? payload.domain.trim() : "",
        priority: payload?.priority ?? "low",
        destinationCountries: payload?.countries ?? [],
      }),
    )
      .unwrap()
      .then(() => {
        dispatch(
          showNotification({
            title: t("success"),
            message: t(isEdit ? "entityUpdateSuccess" : "entityCreateSuccess"),
            type: NotificationTypes.success,
          }),
        );
        handleClose();
      })
      .catch((error) => {
        handleApiError({ error, t, dispatch });
      });
  };

  const handleUpdateField = <
    K extends keyof Pick<
      MirrorItem,
      "domain" | "title" | "priority" | "countries"
    >,
  >(
    key: K,
    value: MirrorItem[K],
  ) => {
    const updatedField = { [key]: value };
    dispatch(handleUpdateDraftItem(updatedField));
  };

  useEffect(() => {
    if (isEdit && entityId && !isItemFetched) {
      dispatch(getMirrorItem(entityId))
        .unwrap()
        .catch(() => {
          setLoadFailed(true);
          dispatch(
            showNotification({
              title: t("error"),
              message: t("entityUpdateError"),
              type: NotificationTypes.error,
            }),
          );
        });
    }
  }, [dispatch, entityId, isEdit, isItemFetched, t]);

  return (
    <>
      <Modal onClose={handleClose} size="sm" className={styles.mirrorModal}>
        <Modal.Header>{t("mirror")}</Modal.Header>
        <Modal.Content>
          {isEdit && !isItemFetched ? (
            loadFailed ? (
              <div>{t("entityUpdateError")}</div>
            ) : (
              <ModalTabSkeleton />
            )
          ) : (
            <div className={styles.createForm}>
              <div className="bonus-form-box--item">
                <InnerInput
                  value={payload?.domain ?? ""}
                  placeholder={t("Domain")}
                  label={t("Domain")}
                  type="text"
                  disabled={isEdit}
                  id="mirror-title"
                  onChange={(v) => handleUpdateField("domain", v)}
                />
              </div>
              <div className="bonus-form-box--item">
                <InnerInput
                  value={payload?.title ?? ""}
                  placeholder={t("Title")}
                  label={t("title")}
                  type="text"
                  id="mirror-domain"
                  onChange={(v) => handleUpdateField("title", v)}
                />
              </div>
              <div className="bonus-form-box--item">
                <DefaultDropdown
                  value={payload?.priority ?? "low"}
                  onSelect={(v) => handleUpdateField("priority", v as any)}
                  placeholder={t("Select priority")}
                  label={t("Priority")}
                  options={[
                    { value: "low", label: "Low" },
                    { value: "medium", label: "Medium" },
                    { value: "high", label: "High" },
                  ]}
                  isShort
                />
              </div>
              <div className={`bonus-form-box--item ${styles.countriesField}`}>
                <label className="default-dropdown__label">
                  {t("countries")}
                </label>
                <SearchDropdown
                  label={t("selectCountry")}
                  items={
                    countries?.map((country) => ({
                      value: country.code,
                      label: country.name,
                      icon: `https://flagpedia.net/data/flags/icon/16x12/${country.code.toLowerCase()}.png`,
                    })) ?? []
                  }
                  defaultValues={(payload?.countries ?? []).map((code) => ({
                    value: code,
                    label:
                      countries?.find((country) => country.code === code)
                        ?.name ?? code,
                    icon: `https://flagpedia.net/data/flags/icon/16x12/${code.toLowerCase()}.png`,
                  }))}
                  onSelect={(item) => {
                    const codes = payload?.countries ?? [];
                    const code = String(item.value);
                    handleUpdateField(
                      "countries",
                      item.removed
                        ? codes.filter((value) => value !== code)
                        : Array.from(new Set([...codes, code])),
                    );
                  }}
                  visibleItems={10}
                />
              </div>
            </div>
          )}
        </Modal.Content>
        <Modal.Footer>
          <Button isSecondary txt={t("cancel")} handleClick={handleClose} />
          <Button
            isPrimary
            handleClick={handleSave}
            txt={t("save")}
            isLoad={isItemFetching}
            disabled={isEdit && !isItemFetched}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default MirrorCreateModal;
